import React, { FC, useState } from 'react';
import { Button, Switch, useTheme, Box } from '@mui/material';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import DehazeIcon from '@mui/icons-material/Dehaze';

import { boxCss, descriptionCss } from '../../common/style';
import { BuilderComponentProps, useNode } from '@mnz/builder';
import { Plus } from '@menuzen/icon/build/outline';
import { px2rem } from '../../core';
import ChevronRightIcon from '../../common/icon/ChevronDownRight';
import { capitalizeFirstLetter } from '../../shared/utils/string';
import LabelIcon from '../../common/components/label-icon/LabelIcon';
import GradientIconButton from '../../common/components/button/GradientIconButton';
import SwipeToDelete from '../../common/components/swipe2delete/SwipeToDelete';
import EditOrderLinkModal from './EditOrderLinkModal';
import { _order, reorder } from '../../shared/utils';
import { useDragAndDrop } from '../../shared/hooks/useDragAndDrop';

export interface LinksSettingProps extends BuilderComponentProps {
    hideOrderItems: Record<number, boolean>;
    order?: number[];
}

const OrderLinkSetting: FC<LinksSettingProps> = () => {
    const theme = useTheme();
    const [openModalEditOrderLink, setOpenModalEditOrderLink] = useState(false);
    const [selectedOrderLink, setSelectedOrderLink] = useState(null);

    const { dragCss, placeholderCss, onDragEnd: handleOnDragEnd, onDragUpdate } = useDragAndDrop();

    const openEditModalOrderLink = (orderLink) => {
        setSelectedOrderLink(orderLink);
        setOpenModalEditOrderLink(true);
    };

    const {
        actions: { setProp, setAccount },
        props: { account, order: defaultOrder, ...props },
    } = useNode((node) => ({
        hidden: node.data.hidden,
        props: node.data.props as unknown as LinksSettingProps,
    }));

    const [order, setOrder] = useState(defaultOrder || []);

    const handleDeleteOrderLink = async (id: number) => {
        setAccount({
            orderLinks: account.orderLinks.filter((link) => link.id !== id),
        });
        setSelectedOrderLink(null);
        setOpenModalEditOrderLink(false);
    };

    const orderLinks = _order(account?.orderLinks, order || []);

    const onDragEnd = (res: DropResult) => {
        if (!res.destination) {
            return;
        }
        handleOnDragEnd();
        const newOrderLink: any[] = reorder(orderLinks, res.source.index, res.destination.index);
        const newOrder = newOrderLink.map((order: any) => order.id);

        setProp<LinksSettingProps>((props) => {
            props.order = newOrder;
        });
        setOrder(newOrder);
    };

    return (
        <div css={{ marginTop: px2rem(20), marginBottom: px2rem(16) }}>
            <EditOrderLinkModal
                open={openModalEditOrderLink}
                selectedOrderLink={selectedOrderLink}
                setSelectedOrderLink={setSelectedOrderLink}
                onClose={() => setOpenModalEditOrderLink(false)}
            />
            <DragDropContext onDragEnd={onDragEnd} onDragUpdate={onDragUpdate}>
                <Droppable droppableId="orderLink-droppable">
                    {(provided) => (
                        <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            css={{ overflow: 'auto', maxHeight: '450px', position: 'relative' }}
                        >
                            {orderLinks.map((orderLink, index) => (
                                <Draggable key={orderLink.id} draggableId={`${orderLink.id}`} index={index}>
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            css={{ marginBottom: 10 }}
                                        >
                                            <div
                                                css={[
                                                    snapshot.isDragging ? dragCss : {},
                                                    boxCss(theme),
                                                    {
                                                        padding: '10px 16px',
                                                        backgroundColor: '#fff',
                                                        overflow: 'hidden',
                                                    },
                                                ]}
                                            >
                                                <SwipeToDelete
                                                    onDelete={() => {
                                                        handleDeleteOrderLink(orderLink.id);
                                                    }}
                                                >
                                                    <div
                                                        css={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            backgroundColor: 'white',
                                                        }}
                                                    >
                                                        <div className="text-16 mr-8">
                                                            <DehazeIcon
                                                                sx={{
                                                                    color: (theme) => theme.palette.primary50,
                                                                    fontSize: '16px',
                                                                }}
                                                            />
                                                        </div>
                                                        <GradientIconButton
                                                            onClick={() => openEditModalOrderLink(orderLink)}
                                                            style={{
                                                                fontSize: px2rem(14),
                                                            }}
                                                        >
                                                            <LabelIcon
                                                                type={'outline'}
                                                                icon={capitalizeFirstLetter(orderLink.type)}
                                                                size={20}
                                                            />
                                                        </GradientIconButton>
                                                        <div
                                                            css={{ flex: '1 1 auto', paddingLeft: px2rem(8) }}
                                                            onClick={() => openEditModalOrderLink(orderLink)}
                                                        >
                                                            <div css={{ lineHeight: px2rem(16) }}>
                                                                {capitalizeFirstLetter(orderLink.type)}
                                                            </div>
                                                            <div
                                                                css={[
                                                                    descriptionCss(theme),
                                                                    { lineHeight: px2rem(12), fontSize: px2rem(10) },
                                                                ]}
                                                            >
                                                                {orderLink.link}
                                                            </div>
                                                        </div>
                                                        <div
                                                            css={{
                                                                display: 'flex',
                                                                flexShrink: 0,
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <Switch
                                                                checked={
                                                                    props.hideOrderItems &&
                                                                    typeof props.hideOrderItems[orderLink.id] !==
                                                                        'undefined'
                                                                        ? !props.hideOrderItems[orderLink.id]
                                                                        : true
                                                                }
                                                                onChange={(e) =>
                                                                    setProp<LinksSettingProps>((props) => {
                                                                        if (
                                                                            typeof props.hideOrderItems === 'undefined'
                                                                        ) {
                                                                            props.hideOrderItems = {};
                                                                        }
                                                                        if (
                                                                            typeof props.hideOrderItems[
                                                                                orderLink.id
                                                                            ] === 'undefined'
                                                                        ) {
                                                                            props.hideOrderItems[orderLink.id] = true;
                                                                        }
                                                                        props.hideOrderItems[orderLink.id] =
                                                                            !e.target.checked;
                                                                    })
                                                                }
                                                            />
                                                            <div
                                                                css={{
                                                                    color: theme.palette.primary50,
                                                                    fontSize: px2rem(20),
                                                                    paddingLeft: px2rem(10),
                                                                }}
                                                                onClick={() => openEditModalOrderLink(orderLink)}
                                                            >
                                                                <ChevronRightIcon />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </SwipeToDelete>
                                            </div>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}

                            <Box
                                sx={{
                                    ...placeholderCss,
                                    borderRadius: '8px',
                                }}
                            />
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            {account.orderLinks.length < 20 && (
                <div css={{ paddingTop: px2rem(6) }}>
                    <Button
                        variant="dashed"
                        color="success"
                        fullWidth
                        onClick={() => setOpenModalEditOrderLink(true)}
                        style={{ borderRadius: px2rem(8), borderWidth: 1, padding: '15px 20px' }}
                    >
                        <Plus />
                        <span css={{ paddingLeft: px2rem(10) }}>New Ordering Link</span>
                    </Button>
                </div>
            )}
        </div>
    );
};

export default OrderLinkSetting;
